/**
 * Tijdweergave op de tablet.
 *
 * Let op het verschil met de backend: die rekent expliciet in Europe/Amsterdam,
 * omdat een Worker in UTC draait. Hier gebruiken we juist wél de lokale klok van
 * het apparaat, want dat *is* de klok die de leerling naast het rooster ziet
 * hangen. De lestijden komen als 'HH:MM' in schooltijd uit de backend, dus die
 * twee vergelijken klopt zolang de tablet op Nederlandse tijd staat — staat een
 * tablet verkeerd ingesteld, dan staat de NU-markering verkeerd. Dat is een punt
 * voor het MDM-profiel, niet iets wat de app kan repareren.
 */

export const DAGEN = [
  'zondag',
  'maandag',
  'dinsdag',
  'woensdag',
  'donderdag',
  'vrijdag',
  'zaterdag',
] as const;

export const MAANDEN = [
  'januari',
  'februari',
  'maart',
  'april',
  'mei',
  'juni',
  'juli',
  'augustus',
  'september',
  'oktober',
  'november',
  'december',
] as const;

export const pad = (n: number): string => String(n).padStart(2, '0');

/** 'HH:MM' → minuten sinds middernacht. */
export function minuten(hhmm: string): number {
  const [u = '0', m = '0'] = String(hhmm).split(':');
  return Number(u) * 60 + Number(m);
}

/** Lesduur in hele minuten, uit de twee 'HH:MM'-tijden die de backend levert. */
export const duurMinuten = (van: string, tot: string): number =>
  Math.max(0, minuten(tot) - minuten(van));

/** Minuten sinds middernacht → 'HH:MM'. */
export function naarHhmm(min: number): string {
  return `${pad(Math.floor(min / 60))}:${pad(min % 60)}`;
}

export const klokTijd = (d: Date): string =>
  `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

export const klokDatum = (d: Date): string =>
  `${DAGEN[d.getDay()]} ${d.getDate()} ${MAANDEN[d.getMonth()]}`;

/** Minuten sinds middernacht van een moment. */
export const minutenVan = (d: Date): number => d.getHours() * 60 + d.getMinutes();

/** Een ISO-tijdstempel als 'HH:MM' voor de "bijgewerkt"-melding. */
export function tijdstempelKort(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
}

/** Een datum als 'YYYY-MM-DD' in de lokale tijdzone van de tablet. */
export const dagISO = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** 'YYYY-MM-DD' → Date op middernacht lokale tijd. */
export function vanDagISO(dag: string): Date {
  const [j = 1970, m = 1, d = 1] = dag.split('-').map(Number);
  return new Date(j, m - 1, d);
}

/**
 * De eerstvolgende schooldag na een dag: weekenden worden overgeslagen.
 *
 * Dat is dezelfde regel als in de sync aan de serverkant (api/src/lib/venster.js,
 * SLA_WEEKEND_OVER): vraagt de tablet op vrijdag om "morgen", dan is dat maandag,
 * en dat is precies de dag die de Worker dan ook heeft opgehaald. Liepen die twee
 * uiteen, dan vroeg de tablet een dag op waarvoor niets in D1 staat en zou het
 * scherm een lege zaterdag tonen alsof de leerling vrij was.
 */
export function volgendeSchooldag(dag: string): string {
  const d = vanDagISO(dag);
  do {
    d.setDate(d.getDate() + 1);
  } while (d.getDay() === 0 || d.getDay() === 6);
  return dagISO(d);
}

/**
 * De groet boven het rooster: vóór 12:00 "Goedemorgen", tot 18:00 "Goedemiddag",
 * daarna "Goedenavond". Rekent met de tijd die het scherm toont, dus in een demo
 * ook met de demo-tijd.
 */
export function begroeting(d: Date): string {
  const uur = d.getHours();
  if (uur < 12) return 'Goedemorgen';
  if (uur < 18) return 'Goedemiddag';
  return 'Goedenavond';
}

/** 'YYYY-MM-DD' → 'maandag 31 augustus'. */
export const dagDatum = (dag: string): string => klokDatum(vanDagISO(dag));
